import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AppLayout from '../../components/AppLayout';
import { getCompanyProfile, updateCompanyProfile } from '../../services/companyService';

const defaultHosNotificationSettings = {
  enabled: true,
  inAppEnabled: true,
  fcmEnabled: true,
};

const formatHours = (value) => {
  const minutes = Math.max(0, Math.round(Number(value || 0) * 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const StatCell = ({ label, value, hint, tone }) => (
  <div className={`intel-stat-cell ${tone ? `intel-stat-cell--${tone}` : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {hint ? <em>{hint}</em> : null}
  </div>
);

export default function HOSNotifications() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadCompany = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getCompanyProfile();
      setCompany(response.data?.company || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load HOS notification controls.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  const settings = company?.hosNotificationSettings || defaultHosNotificationSettings;
  const hosSettings = company?.hosSettings || {};

  const setField = (field, value) => {
    setSuccess('');
    setError('');
    setCompany((prev) => ({
      ...(prev || {}),
      hosNotificationSettings: {
        ...defaultHosNotificationSettings,
        ...(prev?.hosNotificationSettings || {}),
        [field]: value,
      },
    }));
  };

  const saveSettings = async () => {
    if (!company) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await updateCompanyProfile({
        hosNotificationSettings: {
          ...defaultHosNotificationSettings,
          ...(company.hosNotificationSettings || {}),
        },
      });
      setCompany(response.data?.company || company);
      setSuccess('HOS notification controls saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save HOS notification controls.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout
      title="HOS reminders & notifications"
      subtitle="Control HOS reminder delivery without changing HOS tracking, rules, or documentation."
      actions={<Link to="/reports/builder?template=hours-of-service" className="btn btn-subtle">HOS report</Link>}
    >
      <div className="panel">
        <div className="panel-header">
          <h3>Notification delivery</h3>
        </div>
        <p className="intel-panel-note">
          These controls only affect reminder delivery. Hours of Service tracking and documentation continue even when notifications are off.
        </p>

        {loading ? (
          <div className="skeleton" style={{ height: 220, borderRadius: 12 }} />
        ) : (
          <>
            <div className="intel-stat-grid">
              <StatCell label="Warning threshold" value={formatHours(hosSettings.ALERT_THRESHOLD_HOURS ?? 11.5)} />
              <StatCell label="Max on duty" value={formatHours(hosSettings.MAX_ON_DUTY_HOURS ?? 12)} />
              <StatCell label="In-app" value={settings.enabled && settings.inAppEnabled ? 'On' : 'Off'} tone={settings.enabled && settings.inAppEnabled ? 'good' : 'warn'} />
              <StatCell label="FCM push" value={settings.enabled && settings.fcmEnabled ? 'On' : 'Off'} tone={settings.enabled && settings.fcmEnabled ? 'good' : 'warn'} />
            </div>

            <div className="intel-control-grid">
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={Boolean(settings.enabled)}
                  onChange={(event) => setField('enabled', event.target.checked)}
                />
                <span>
                  <strong>Enable HOS reminder delivery</strong>
                  <small>Turn this off to suppress all HOS reminder notifications while still documenting threshold events.</small>
                </span>
              </label>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={Boolean(settings.inAppEnabled)}
                  onChange={(event) => setField('inAppEnabled', event.target.checked)}
                />
                <span>
                  <strong>In-app messages</strong>
                  <small>Send connected drivers a real-time HOS reminder inside the driver app.</small>
                </span>
              </label>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={Boolean(settings.fcmEnabled)}
                  onChange={(event) => setField('fcmEnabled', event.target.checked)}
                />
                <span>
                  <strong>FCM push notifications</strong>
                  <small>Send HOS reminders to registered devices when push delivery is available.</small>
                </span>
              </label>
            </div>

            {error ? <div className="feedback error">{error}</div> : null}
            {success ? <div className="feedback success">{success}</div> : null}

            <div className="panel-footer" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={saving}>
                {saving ? 'Saving...' : 'Save HOS notification controls'}
              </button>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
